import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useApolloClient } from '@apollo/client';
import ParagraphWelcome from './ParagraphWelcome';
import ParagraphReading from './ParagraphReading';
import { GET_PARAGRAPHS } from '../../graphql/paragraph-queries';
import { ensureParagraphsSeed } from './paragraphsStore';
import { useGradeStream } from '../../hooks/useGradeStream';

/**
 * ParagraphMode — orchestrator for the Reading flow.
 * Stages: list → read → translate → feedback.
 */
function ParagraphMode({ onBack }) {
  const client = useApolloClient();
  const { data, loading, error, refetch } = useQuery(GET_PARAGRAPHS, {
    fetchPolicy: 'cache-and-network',
  });
  const grading = useGradeStream();

  const [selectedPassage, setSelectedPassage] = useState(null);
  const [stage, setStage] = useState('list');
  const [studentTranslation, setStudentTranslation] = useState('');
  const seededRef = useRef(false);

  const passages = data?.paragraphs || [];

  // One-shot seed/migration if the teacher has no paragraphs yet.
  useEffect(() => {
    if (loading || seededRef.current) return;
    if (passages.length === 0) {
      seededRef.current = true;
      ensureParagraphsSeed(client, passages).then(() => refetch());
    }
  }, [loading, passages, client, refetch]);

  const handleSelectPassage = useCallback((passage) => {
    setSelectedPassage(passage);
    setStudentTranslation('');
    grading.reset();
    setStage('read');
  }, [grading]);

  const handleStartTranslate = useCallback(() => setStage('translate'), []);

  const handleSubmit = useCallback(() => {
    if (!selectedPassage) return;
    grading.reset();
    grading.submit({
      paragraphId: selectedPassage.id,
      translation: studentTranslation,
    });
    // Stay on the translate stage; the inline grading indicator shows under
    // the submit button. We advance to 'feedback' from the useEffect below.
  }, [selectedPassage, studentTranslation, grading]);

  // When the stream completes (success or error), advance the stage.
  useEffect(() => {
    if (grading.status === 'done' && grading.feedback) {
      setStage('feedback');
    }
  }, [grading.status, grading.feedback]);

  const handleRevise = useCallback(() => {
    grading.reset();
    setStage('translate');
  }, [grading]);
  const handleBackToRead = useCallback(() => setStage('read'), []);

  const handleTryAnother = useCallback(() => {
    setSelectedPassage(null);
    setStudentTranslation('');
    grading.reset();
    setStage('list');
  }, [grading]);

  const handleLeave = handleTryAnother;

  if (loading && passages.length === 0) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--paper)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div className="t-eyebrow" style={{ marginBottom: 12 }}>
            Reading · 阅读
          </div>
          <h1 className="t-display">Loading passages…</h1>
        </div>
      </div>
    );
  }

  if (error && passages.length === 0) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--paper)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 32,
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: 480 }}>
          <div className="t-eyebrow" style={{ marginBottom: 12, color: 'var(--vermillion)' }}>
            Couldn't load passages
          </div>
          <p className="t-body" style={{ marginBottom: 16 }}>{error.message}</p>
          <button className="btn" onClick={() => refetch()}>Retry</button>
        </div>
      </div>
    );
  }

  if (stage === 'list' || !selectedPassage) {
    return (
      <ParagraphWelcome
        passages={passages}
        onSelect={handleSelectPassage}
        onBack={onBack}
      />
    );
  }

  return (
    <ParagraphReading
      stage={stage}
      passage={selectedPassage}
      studentTranslation={studentTranslation}
      setStudentTranslation={setStudentTranslation}
      feedback={grading.feedback}
      submitting={grading.status === 'streaming'}
      submitError={grading.status === 'error' ? grading.error : null}
      thinking={grading.thinking}
      onTranslate={handleStartTranslate}
      onSubmit={handleSubmit}
      onRevise={handleRevise}
      onBackToRead={handleBackToRead}
      onTryAnother={handleTryAnother}
      onLeave={handleLeave}
    />
  );
}

export default ParagraphMode;
